import { formatSecondsToHHMMSS } from "@utils/utils";
import { format, parseISO } from "date-fns";
import React, { useEffect, useState } from "react";

import { CalendarDateRange, Column, SearchableDropdownItem } from "@fronterahealth/frontera-ui-components";

import { useBcbaResponsesToRbtQuestionsRawDataQuery } from "@api/graphql/types-and-hooks";
import { DialogFilterBar } from "@pages/VideoAnalytics/components/DialogFilterBar";
import { RawDataTableWrapper } from "@pages/VideoAnalytics/components/RawDataTableWrapper";
import { DATE_FORMAT, PAGE_SIZE } from "@pages/VideoAnalytics/helpers";
import { useVideoAnalytics } from "@providers/VideoAnalyticsProvider";

interface BCBAResponseToRBTQuestionRawDataProps {
  isResponseRate: boolean;
}
type bcbaResponseToRBTRawDataType = {
  rbtName: string;
  questionAskedAt: string;
  respondedOn: string;
  responseTimeInSeconds: number;
  bcbaName: string;
};

export const BCBAResponseToRBTQuestionRawData: React.FC<BCBAResponseToRBTQuestionRawDataProps> = ({
  isResponseRate,
}) => {
  const { selectedDates, selectedBCBA: selectedBCBAList } = useVideoAnalytics();
  const [dates, setDates] = useState<CalendarDateRange | undefined>(selectedDates);
  const [selectedBCBA, setSelectedBCBA] = useState<SearchableDropdownItem[]>(selectedBCBAList);
  const [selectedClient, setSelectedClient] = useState<SearchableDropdownItem[]>([]);

  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

  useEffect(() => {
    setPageNo(1);
  }, [dates, selectedBCBA, selectedClient]);

  const { data, isFetching } = useBcbaResponsesToRbtQuestionsRawDataQuery(
    {
      fromDate: dates?.startDate ? format(dates.startDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      toDate: dates?.endDate ? format(dates.endDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      bcbaProviderIds: selectedBCBA.map((item) => item.id),
      learnerIds: selectedClient.map((item) => item.id),
      offset: pageNo * pageSize - pageSize,
      count: pageSize,
      forResponseRate: isResponseRate,
    },
    {
      queryKey: ["videosReviewedRawData", dates, selectedBCBA, pageNo, pageSize, selectedClient],
    },
  );

  const columns: Column<bcbaResponseToRBTRawDataType>[] = [
    {
      title: "RBT",
      key: "rbtName",
      dataIndex: "rbtName",
      render: (value) => {
        return value || "-";
      },
    },
    {
      title: "Question Asked (Date/time)",
      key: "questionAskedAt",
      dataIndex: "questionAskedAt",
      render: (value) => {
        if (value) {
          const parsedDate = parseISO(value as string);
          const formattedDate = format(parsedDate, "yyyy-MM-dd HH:mm");
          return formattedDate;
        }
        return "-";
      },
    },
    {
      title: "Responded On (Date/time)",
      key: "respondedOn",
      dataIndex: "respondedOn",
      render: (value) => {
        if (value) {
          const parsedDate = parseISO(value as string);
          const formattedDate = format(parsedDate, "yyyy-MM-dd HH:mm");
          return formattedDate;
        }
        return "-";
      },
    },
    {
      title: "Elapsed Response Time",
      key: "responseTimeInSeconds",
      dataIndex: "responseTimeInSeconds",
      render: (value) => {
        return value ? formatSecondsToHHMMSS(value as number) : "-";
      },
    },
    {
      title: "BCBA",
      key: "bcba",
      dataIndex: "bcbaName",
      render: (value) => {
        return value || "-";
      },
    },
  ];

  return (
    <div className="grid space-y-2">
      <DialogFilterBar
        dates={dates}
        setDates={setDates}
        selectedBCBA={selectedBCBA}
        setSelectedBCBA={setSelectedBCBA}
        selectedClients={selectedClient}
        setSelectedClients={setSelectedClient}
        showClientFilter={false}
      />
      <RawDataTableWrapper
        data={
          data && data.bcbaResponsesToRbtQuestionsRawData?.rawData
            ? (data.bcbaResponsesToRbtQuestionsRawData?.rawData as bcbaResponseToRBTRawDataType[])
            : []
        }
        columns={columns}
        loading={isFetching}
        totalItemCount={data?.bcbaResponsesToRbtQuestionsRawData?.totalRecordsCount || 0}
        defaultItemsPerPage={pageSize}
        currentPage={pageNo}
        onPageChange={(current, itemsPerPage) => {
          setPageNo(current);
          setPageSize(itemsPerPage);
        }}
      />
    </div>
  );
};
